import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import ReportPdfDialog from '../components/reports/ReportPdfDialog';

interface ReportViewerContextValue {
  /**
   * Opens the global report viewer modal for the given document GUID. Pass the
   * document's key so the viewer's Print marks it Printed in the Print Queue.
   */
  openReport: (guid: string, reportName?: string, reportDocumentId?: number) => void;
  /** Closes the modal. Rarely needed directly — the dialog's own close
   *  affordances (X, backdrop click) already call this. */
  closeReport: () => void;
}

const ReportViewerContext = createContext<ReportViewerContextValue | null>(null);

/**
 * App-root provider that owns the single shared report viewer
 * (`<ReportPdfDialog>`, pdf.js). Any component in the tree can call
 * `useReportViewer()` to pop the viewer without owning a `<Dialog>` of its
 * own. There is exactly one dialog mounted app-wide.
 */
export function ReportViewerProvider({ children }: { children: ReactNode }) {
  const [guid, setGuid] = useState<string | null>(null);
  const [reportName, setReportName] = useState<string | undefined>(undefined);
  const [reportDocumentId, setReportDocumentId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const openReport = useCallback((g: string, name?: string, id?: number) => {
    setGuid(g);
    setReportName(name);
    setReportDocumentId(id ?? null);
    setOpen(true);
  }, []);

  const closeReport = useCallback(() => {
    setOpen(false);
    // Clear guid after close so the viewer drops the document. Delay to next tick so the MUI fade-out animation still has
    // the viewer in the DOM.
    setTimeout(() => {
      setGuid(null);
      setReportName(undefined);
      setReportDocumentId(null);
    }, 0);
  }, []);

  const value = useMemo(() => ({ openReport, closeReport }), [openReport, closeReport]);

  return (
    <ReportViewerContext.Provider value={value}>
      {children}
      <ReportPdfDialog
        open={open}
        guid={guid}
        reportName={reportName}
        reportDocumentId={reportDocumentId}
        onClose={closeReport}
      />
    </ReportViewerContext.Provider>
  );
}

/**
 * Read the report viewer's `openReport(guid, name?, reportDocumentId?)` action. Throws
 * if used outside `<ReportViewerProvider>` — catches the bug where
 * someone renders a call site above the provider by accident.
 */
export function useReportViewer(): ReportViewerContextValue {
  const ctx = useContext(ReportViewerContext);
  if (!ctx) {
    throw new Error('useReportViewer must be used inside <ReportViewerProvider>');
  }
  return ctx;
}
